import { useState, useRef, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { withSearchParam, type LocationStateWithFrom } from '@/lib/navigation'
import { useNotifications } from './NotificationProvider'
import { confClass, DetectionChips, AudioChips, CameraInfo, type DetectionNotification } from './shared'
import { useEventTitle } from '@/hooks/useEventTitle'  // #294

// ── Single peek card ────────────────────────────────────────────────────────

function PeekCard({ notif, onDismiss, onPause, onResume, shouldExit }: {
  notif: DetectionNotification
  onDismiss: (id: string) => void
  onPause: (id: string) => void
  onResume: (id: string) => void
  shouldExit?: boolean
}) {
  const { t } = useTranslation('notifications')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const [exiting, setExiting] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const swipeRef = useRef({ startX: 0, currentX: 0, dragging: false })

  const isAlert = notif.variant === 'alert'
  // #337 — a critical event's toast: red, "CRITICAL" before its title (the critical rows' label), no progress bar
  // (no timer: the user closes it)
  const critical = notif.critical === true
  const criticalLabel = critical ? <span className="det-critical-label">{tc('critical.label')}</span> : null
  const pct = notif.confidence !== undefined ? Math.round(notif.confidence * 100) : null
  // #294 — a critical event: the critical title ("Graffiti spray sound · 64 %"), never "Motion detected"
  const eventTitle = useEventTitle()
  const title = eventTitle({ ...notif.event, event_type: notif.eventType }, notif.title)

  const handleDismiss = useCallback(() => {
    if (exiting) return
    setExiting(true)
    setTimeout(() => onDismiss(notif.id), 300)
  }, [exiting, notif.id, onDismiss])

  const handleCardClick = useCallback(() => {
    // Guard against accidental click after a swipe attempt (any horizontal motion).
    const dx = Math.abs(swipeRef.current.currentX - swipeRef.current.startX)
    if (dx > 5) return
    const eventId = notif.event?.id
    if (typeof eventId !== 'number') return
    // If the user is already on /events, keep their filters — a bare `?event=N`
    // search REPLACES the query string rather than merging into it, and wiping
    // the filter set on every notification click is the #078 bug we are fixing.
    const onEvents = location.pathname === '/events'
    const current = onEvents ? location.search : ''
    navigate(
      { pathname: '/events', search: withSearchParam(current, 'event', String(eventId)) },
      // Already on /events? This is a same-pathname PUSH of a lightbox entry, so mark
      // it: closing must POP it, not strip the param, or the entry is left dead in the
      // stack and the next Back press does nothing. Coming from another page it is a
      // real page transition — no marker, so closing reveals the feed and Back still
      // returns to the page the user came from.
      onEvents
        ? {
            state: {
              ...(location.state as LocationStateWithFrom | null),
              lightboxPushed: true,
            } satisfies LocationStateWithFrom,
          }
        : undefined,
    )
    handleDismiss()
  }, [navigate, location.pathname, location.search, location.state, notif.event, handleDismiss])

  // Timer expired — trigger exit animation before removal
  useEffect(() => {
    if (shouldExit && !exiting) handleDismiss()
  }, [shouldExit, exiting, handleDismiss])

  // ── Swipe handlers ──
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    swipeRef.current = { startX: e.clientX, currentX: e.clientX, dragging: true }
    cardRef.current?.setPointerCapture(e.pointerId)
    cardRef.current?.classList.add('peek-swiping')
    onPause(notif.id)
  }, [notif.id, onPause])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!swipeRef.current.dragging) return
    swipeRef.current.currentX = e.clientX
    const dx = e.clientX - swipeRef.current.startX
    if (dx > 0 && cardRef.current) {
      cardRef.current.style.transform = `translateX(${dx}px)`
      cardRef.current.style.opacity = `${Math.max(0, 1 - dx / 200)}`
    }
  }, [])

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!swipeRef.current.dragging) return
    swipeRef.current.dragging = false
    cardRef.current?.classList.remove('peek-swiping')
    cardRef.current?.releasePointerCapture(e.pointerId)
    const dx = swipeRef.current.currentX - swipeRef.current.startX
    if (dx > 80) {
      handleDismiss()
    } else {
      if (cardRef.current) {
        cardRef.current.style.transform = ''
        cardRef.current.style.opacity = ''
      }
      onResume(notif.id)
    }
  }, [handleDismiss, notif.id, onResume])

  return (
    <div
      ref={cardRef}
      className={cn(
        'peek-card-wrapper',
        'peek-card-clickable',
        isAlert ? 'peek-alert-wrapper' : 'peek-standard-wrapper',
        critical && 'peek-critical',
        exiting && 'peek-exiting',
      )}
      onMouseEnter={() => onPause(notif.id)}
      onMouseLeave={() => onResume(notif.id)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
    >
      <div className={isAlert ? 'det-alert-card' : 'det-standard-card'}>
        {isAlert ? (
          <>
            {/* Alert header */}
            <div className="det-alert-header">
              <div className="det-live-badge">
                <span className="det-live-dot" />
                {t('peek.liveDetection')}
              </div>
              <div className="det-alert-header-right">
                {pct !== null && (
                  <span className={cn('det-conf-badge', confClass(notif.confidence!))}>{pct}%</span>
                )}
                {notif.timestamp && <span className="det-timestamp">{notif.timestamp}</span>}
                <button
                  className="det-close-btn"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); handleDismiss() }}
                  aria-label={t('peek.dismiss')}
                >
                  <X size={12} />
                </button>
              </div>
            </div>

            {/* Alert body */}
            <div className="det-alert-body">
              {notif.thumbnailUrl && (
                <div className="det-alert-thumb">
                  <img
                    src={notif.thumbnailUrl}
                    alt={title}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                </div>
              )}
              <div className="det-alert-content">
                <p className="det-alert-title">{criticalLabel}{title}</p>
                <CameraInfo camera={notif.camera} group={notif.cameraGroup} />
                <DetectionChips objects={notif.detectedObjects} detectionMap={notif.detectionMap} />
                <AudioChips detections={notif.audioDetections} />
              </div>
            </div>

            {/* Progress bar (#337: none on a critical toast, it has no timer) */}
            {!critical && (
              <div className="det-progress-bar">
                <div className="det-progress-fill det-progress-alert" />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Standard inner */}
            <div className="det-standard-inner">
              {notif.thumbnailUrl && (
                <div className="det-standard-thumb">
                  <img
                    src={notif.thumbnailUrl}
                    alt={title}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                </div>
              )}
              <div className="det-standard-content">
                <div className="det-standard-top">
                  <p className="det-standard-title">{criticalLabel}{title}</p>
                  <div className="det-standard-top-right">
                    {pct !== null && (
                      <span className={cn('det-conf-badge det-conf-badge-sm', confClass(notif.confidence!))}>{pct}%</span>
                    )}
                    <button
                      className="det-close-btn"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); handleDismiss() }}
                      aria-label={t('peek.dismiss')}
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
                <CameraInfo camera={notif.camera} group={notif.cameraGroup} small />
                <DetectionChips objects={notif.detectedObjects} detectionMap={notif.detectionMap} />
                <AudioChips detections={notif.audioDetections} />
              </div>
            </div>

            {/* Standard footer */}
            <div className="det-standard-footer">
              <div className="det-live-badge det-live-badge-subtle">
                <span className="det-live-dot" />
                {t('peek.live')}
              </div>
              <span className="det-footer-meta">{notif.timestamp ?? tc('time.justNow')}</span>
            </div>

            {/* Progress bar (#337: none on a critical toast, it has no timer) */}
            {!critical && (
              <div className="det-progress-bar">
                <div className="det-progress-fill det-progress-standard" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Peek container ──────────────────────────────────────────────────────────

export function PeekCards() {
  const { notifications, peekIds, exitingPeekIds, dismissPeek, pausePeekTimer, resumePeekTimer } = useNotifications()

  if (peekIds.length === 0) return null

  return (
    <div className="peek-container">
      {peekIds.map(id => {
        const notif = notifications.find(n => n.id === id)
        if (!notif) return null
        return (
          <PeekCard
            key={id}
            notif={notif}
            onDismiss={dismissPeek}
            onPause={pausePeekTimer}
            onResume={resumePeekTimer}
            shouldExit={exitingPeekIds.includes(id)}
          />
        )
      })}
    </div>
  )
}
